import React, { useState, useEffect, useCallback } from 'react';
import TeamMemberEntry from '../components/TeamMemberEntry/TeamMemberEntry';
import SegmentedControl from '../components/SegmentedControl/SegmentedControl';
import ChipFilter from '../components/ChipFilter/ChipFilter';
import Select from '../components/Select/Select';
import api from '../services/api';
import styles from './MentoringPage.module.scss';

// View grouping options
const GROUP_BY = {
  ALL: 'all',
  GROWTH_ZONE: 'growthZone',
  MENTORING: 'mentoring'
};

function MentoringPage({ skills, skillGroups, levels, teamMembers, currentUser }) {
  const [focusExpertPairings, setFocusExpertPairings] = useState([]);
  const [groupBy, setGroupBy] = useState(GROUP_BY.ALL);
  const [selectedMembers, setSelectedMembers] = useState(['all']);
  const [expertSelections, setExpertSelections] = useState({}); // Track user's expert choices
  
  // Find current user's team member if it exists
  const currentUserTeamMember = useCallback(() => {
    if (!currentUser || !teamMembers.length) return null;
    return teamMembers.find(member => member.email === currentUser.email);
  }, [currentUser, teamMembers]);
  
  // Handle chip filter clicks
  const handleChipClick = (memberId) => {
    if (memberId === 'all') {
      setSelectedMembers(['all']);
    } else {
      const newSelected = selectedMembers.includes('all') 
        ? [memberId]
        : selectedMembers.includes(memberId)
          ? selectedMembers.filter(id => id !== memberId)
          : [...selectedMembers, memberId];
      
      // If nothing is selected after removal, default to 'all'
      setSelectedMembers(newSelected.length ? newSelected : ['all']);
    }
  };
  
  // Load mentor assignments from database
  const loadMentorAssignments = useCallback(async () => {
    try {
      const response = await api.getMentorAssignments();
      const assignments = response.data;
      
      // Convert assignments to the format used by expertSelections state
      const selections = {};
      assignments.forEach(assignment => {
        const pairingKey = `${assignment.skill_id}-${assignment.focus_member_id}`;
        selections[pairingKey] = assignment.mentor_id.toString();
      });
      
      setExpertSelections(selections);
    } catch (error) {
      console.error('Error loading mentor assignments:', error);
    }
  }, []);

  // Handle expert selection changes
  const handleExpertSelection = async (pairingKey, expertId) => {
    // Update local state immediately
    setExpertSelections(prev => ({
      ...prev,
      [pairingKey]: expertId
    }));
    
    // Parse the pairing key to get skill_id and focus_member_id
    const [skillId, focusMemberId] = pairingKey.split('-');
    
    try {
      // Save to database
      await api.saveMentorAssignment({
        focus_member_id: parseInt(focusMemberId),
        mentor_id: parseInt(expertId),
        skill_id: parseInt(skillId)
      });
    } catch (error) {
      console.error('Error saving mentor assignment:', error);
      // Revert the local state change on error
      setExpertSelections(prev => {
        const updated = { ...prev };
        delete updated[pairingKey];
        return updated;
      });
    }
  };
  
  // Get the effective expert for a pairing (user's selection or default)
  const getEffectiveExpert = (pairing) => {
    const pairingKey = `${pairing.skill.id}-${pairing.focusMember.id}`;
    const selectedExpertId = expertSelections[pairingKey];
    
    if (selectedExpertId && pairing.availableExperts) {
      const selectedExpert = pairing.availableExperts.find(expert => expert.id === parseInt(selectedExpertId));
      return selectedExpert || pairing.expert;
    }
    
    return pairing.expert;
  };
  
  // Segmented control options
  const viewSegments = [
    { id: GROUP_BY.ALL, label: 'All', icon: '📊' },
    { id: GROUP_BY.GROWTH_ZONE, label: 'Growth Zone', icon: '🌱' },
    { id: GROUP_BY.MENTORING, label: 'Mentoring', icon: '👨‍🏫' }
  ];

  // Calculate pairings between members with focus status and experts
  const calculateFocusExpertPairings = useCallback(() => {
    const pairings = [];
    // Track expert mentorship load to distribute fairly
    const expertMentorshipCount = {};
    
    // Initialize mentorship count for all experts
    teamMembers.forEach(member => {
      expertMentorshipCount[member.id] = 0;
    });
    
    // Loop through all skills to find focus members and experts
    skills.forEach(skill => {
      const skillId = skill.id;
      
      // Find members with focus status for this skill
      const focusMembers = teamMembers.filter(member => {
        if (!Array.isArray(member.skills)) return false;
        
        return member.skills.some(memberSkill => 
          memberSkill.skill_id === skillId && 
          memberSkill.status === 'focus'
        );
      });
      
      // Skip if no focus members for this skill
      if (focusMembers.length === 0) return;
      
      // Process each focus member
      focusMembers.forEach(focusMember => {
        // Find the focus member's current level for this skill
        const focusMemberSkill = focusMember.skills.find(s => s.skill_id === skillId);
        if (!focusMemberSkill || !focusMemberSkill.level_id) {
          // If focus member doesn't have a level assigned, skip
          return;
        }
        const focusMemberLevelId = focusMemberSkill.level_id;
        
        // Find potential experts - team members with HIGHER level than the focus member
        const potentialExperts = teamMembers.filter(member => {
          if (member.id === focusMember.id) return false; // Can't mentor yourself
          if (!Array.isArray(member.skills)) return false;
          
          const memberSkill = member.skills.find(s => s.skill_id === skillId);
          if (!memberSkill || !memberSkill.level_id) return false;
          
          // Only consider someone an expert if they're at a HIGHER level
          return memberSkill.level_id > focusMemberLevelId;
        });
        
        // If no experts at a higher level, create a self-driven entry
        if (potentialExperts.length === 0) {
          pairings.push({
            skill,
            focusMember,
            expert: null,
            isSelfDriven: true
          });
          return;
        }
        
        // Group experts by skill level (to find the highest ones)
        const expertsByLevel = {};
        potentialExperts.forEach(expert => {
          const expertSkill = expert.skills.find(s => s.skill_id === skillId);
          const levelId = expertSkill.level_id;
          
          if (!expertsByLevel[levelId]) {
            expertsByLevel[levelId] = [];
          }
          expertsByLevel[levelId].push(expert);
        });
        
        // Get the highest available level experts
        const highestLevelId = Math.max(...Object.keys(expertsByLevel).map(Number));
        const highestLevelExperts = expertsByLevel[highestLevelId];
        
        // Sort experts by mentorship load (least loaded first)
        const sortedExperts = [...highestLevelExperts].sort((a, b) => 
          (expertMentorshipCount[a.id] || 0) - (expertMentorshipCount[b.id] || 0)
        );
        
        // Use the expert with the lowest load as the default, but keep all available experts
        const defaultExpert = sortedExperts[0];
        
        // Create the pairing with available experts
        pairings.push({
          skill,
          focusMember,
          expert: defaultExpert,
          availableExperts: sortedExperts, // Include all available experts for selection
          isSelfDriven: false
        });
        
        // Increment the selected expert's mentorship count
        expertMentorshipCount[defaultExpert.id] = (expertMentorshipCount[defaultExpert.id] || 0) + 1;
      });
    });
    
    return pairings;
  }, [skills, teamMembers]);

  useEffect(() => {
    // Calculate pairings when data changes or expert selections change
    const pairings = calculateFocusExpertPairings();
    setFocusExpertPairings(pairings);
    
    // Set default filter to current user's team member if exists
    const userTeamMember = currentUserTeamMember();
    if (userTeamMember) {
      setSelectedMembers([String(userTeamMember.id)]);
    } else {
      setSelectedMembers(['all']);
    }
  }, [calculateFocusExpertPairings, currentUserTeamMember, expertSelections]);

  // Load mentor assignments on component mount
  useEffect(() => {
    loadMentorAssignments();
  }, [loadMentorAssignments]);

  // Find the skill group for a skill
  const getSkillGroup = useCallback((skillId) => {
    const skill = skills.find(s => s.id === skillId);
    if (!skill || !skill.skill_group_id) return null;
    return skillGroups.find(g => g.id === skill.skill_group_id);
  }, [skills, skillGroups]);
  
  // Filter pairings based on selected members and view mode
  const getFilteredPairings = useCallback(() => {
    if (selectedMembers.includes('all')) {
      // Show all pairings regardless of view mode
      return focusExpertPairings;
    }
    
    if (groupBy === GROUP_BY.GROWTH_ZONE) {
      // Only show pairings where the selected members are focus members
      return focusExpertPairings.filter(pairing => 
        selectedMembers.includes(String(pairing.focusMember.id))
      );
    }
    
    if (groupBy === GROUP_BY.MENTORING) {
      // Only show pairings where the selected members are experts
      return focusExpertPairings.filter(pairing => 
        pairing.expert && selectedMembers.includes(String(pairing.expert.id))
      );
    }
    
    // For ALL view, show pairings where selected members are either focus or experts
    return focusExpertPairings.filter(pairing => {
      return (
        selectedMembers.includes(String(pairing.focusMember.id)) ||
        (pairing.expert && selectedMembers.includes(String(pairing.expert.id)))
      );
    });
  }, [focusExpertPairings, selectedMembers, groupBy]);
  
  // Group pairings based on the selected grouping option
  const getGroupedPairings = useCallback(() => {
    const filteredPairings = getFilteredPairings();
    
    if (groupBy === GROUP_BY.ALL) {
      // Group by skill (and skill group)
      const skillGroups = {};
      
      // First, organize by skill group
      filteredPairings.forEach(pairing => {
        const skill = pairing.skill;
        const skillGroup = getSkillGroup(skill.id);
        const groupId = skillGroup ? skillGroup.id : 'ungrouped';
        const groupTitle = skillGroup ? skillGroup.title : 'Ungrouped';
        const groupColor = skillGroup ? skillGroup.color : 'hsl(0, 0%, 95%)';
        
        if (!skillGroups[groupId]) {
          skillGroups[groupId] = {
            id: groupId,
            title: groupTitle,
            color: groupColor,
            skills: {}
          };
        }
        
        if (!skillGroups[groupId].skills[skill.id]) {
          skillGroups[groupId].skills[skill.id] = {
            skill,
            pairings: []
          };
        }
        
        skillGroups[groupId].skills[skill.id].pairings.push(pairing);
      });
      
      // Convert to arrays for rendering
      return Object.values(skillGroups)
        .map(group => ({
          ...group,
          skills: Object.values(group.skills)
        }))
        .filter(group => group.skills.length > 0);
    }
    
    if (groupBy === GROUP_BY.GROWTH_ZONE) {
      // Group by focus member
      const memberGroups = {};
      
      filteredPairings.forEach(pairing => {
        const memberId = pairing.focusMember.id;
        
        if (!memberGroups[memberId]) {
          memberGroups[memberId] = {
            member: pairing.focusMember,
            pairings: []
          };
        }
        
        memberGroups[memberId].pairings.push(pairing);
      });
      
      return Object.values(memberGroups);
    }
    
    if (groupBy === GROUP_BY.MENTORING) {
      // Group by expert
      const expertGroups = {};
      
      // Filter out self-driven entries for Mentoring view
      const mentoringPairings = filteredPairings.filter(p => !p.isSelfDriven);
      
      mentoringPairings.forEach(pairing => {
        const effectiveExpert = getEffectiveExpert(pairing);
        const expertId = effectiveExpert.id;
        
        if (!expertGroups[expertId]) {
          expertGroups[expertId] = {
            expert: effectiveExpert,
            pairings: []
          };
        }
        
        expertGroups[expertId].pairings.push(pairing);
      });
      
      // Filter out empty groups
      return Object.values(expertGroups)
        .filter(group => group.pairings.length > 0);
    }
    
    return [];
  }, [groupBy, getFilteredPairings, getSkillGroup]);

  // Render pairings based on the selected grouping
  const renderPairings = () => {
    const groupedPairings = getGroupedPairings();
    
    if (groupBy === GROUP_BY.ALL) {
      return (
        <>
          {groupedPairings.map(group => (
            <div key={group.id} className={styles['pairing-group']}>
              <h3 className={styles['pairing-group__title']}>
                {group.title}
              </h3>
              
              {group.skills.map(skillData => (
                <div key={skillData.skill.id} className={styles['pairing-skill-group']}>
                  <div className={styles['pairings-list']}>
                    {skillData.pairings.map(pairing => renderPairingItem(pairing))}
                  </div>
                </div>
              ))}
            </div>
          ))}
          
          {groupedPairings.length === 0 && renderNoPairingsMessage()}
        </>
      );
    }
    
    if (groupBy === GROUP_BY.GROWTH_ZONE) {
      return (
        <>
          {groupedPairings.map(memberGroup => (
            <div key={memberGroup.member.id} className={styles['pairing-group']}>
              <h3 className={styles['pairing-group__title']}>
                <div className={styles['pairing-group__member']}>
                  {memberGroup.member.picture_url && (
                    <img 
                      src={memberGroup.member.picture_url} 
                      alt={memberGroup.member.name}
                      className={styles['pairing-group__avatar']}
                    />
                  )}
                  <span>{memberGroup.member.name}</span>
                </div>
              </h3>
              
              <div className={styles['pairings-list']}>
                {memberGroup.pairings.map(pairing => renderPairingItem(pairing))}
              </div>
            </div>
          ))}
          
          {groupedPairings.length === 0 && renderEmptyGrowthZoneMessage()}
        </>
      );
    }
    
    if (groupBy === GROUP_BY.MENTORING) {
      return (
        <>
          {groupedPairings.map(expertGroup => (
            <div 
              key={expertGroup.expert ? expertGroup.expert.id : 'self-driven'} 
              className={styles['pairing-group']}
            >
              <h3 className={styles['pairing-group__title']}>
                <div className={styles['pairing-group__member']}>
                  {expertGroup.expert.picture_url && (
                    <img 
                      src={expertGroup.expert.picture_url} 
                      alt={expertGroup.expert.name}
                      className={styles['pairing-group__avatar']}
                    />
                  )}
                  <span>{expertGroup.expert.name}</span>
                </div>
              </h3>
              
              <div className={styles['pairings-list']}>
                {expertGroup.pairings.map(pairing => renderPairingItem(pairing))}
              </div>
            </div>
          ))}
          
          {groupedPairings.length === 0 && renderEmptyMentoringMessage()}
        </>
      );
    }
    
    return null;
  };
  
  // Render an individual pairing item
  const renderPairingItem = (pairing) => {
    const skillGroup = getSkillGroup(pairing.skill.id);
    const effectiveExpert = getEffectiveExpert(pairing);
    const pairingKey = `${pairing.skill.id}-${pairing.focusMember.id}`;
    const hasMultipleExperts = pairing.availableExperts && pairing.availableExperts.length > 1;
    
    return (
      <div 
        key={`${pairing.skill.id}-${pairing.focusMember.id}-${effectiveExpert?.id || 'null'}`} 
        className={styles['pairing-item']}
        style={{ 
          borderLeft: `4px solid ${skillGroup?.color || 'var(--color-primary)'}` 
        }}
      >
        <div className={styles['pairing-item__skill']}>
          <span className={styles['pairing-item__skill-title']}>
            {pairing.skill.title}
          </span>
        </div>
        
        <div className={styles['pairing-item__members']}>
          <div className={styles['pairing-item__focus']}>
            <div className={styles['pairing-item__label']}>Focus</div>
            <TeamMemberEntry
              member={pairing.focusMember}
              status="focus"
              readOnly={true}
            />
          </div>
          
          <div className={styles['pairing-item__arrow']}>
            {pairing.isSelfDriven ? "⚙️" : "→"}
          </div>
          
          <div className={styles['pairing-item__expert']}>
            <div className={styles['pairing-item__label']}>
              {pairing.isSelfDriven ? "Self-Driven" : "Expert"}
            </div>
            {pairing.isSelfDriven ? (
              <div className={styles['pairing-item__self-driven']}>
                Independent learning
              </div>
            ) : hasMultipleExperts ? (
              <div className={styles['pairing-item__expert-selection']}>
                <Select
                  options={pairing.availableExperts.map(expert => ({
                    value: expert.id.toString(),
                    label: expert.name
                  }))}
                  value={expertSelections[pairingKey] || pairing.expert?.id?.toString()}
                  onChange={(value) => handleExpertSelection(pairingKey, value)}
                  placeholder="Choose expert"
                  showSearch={false}
                  className={styles['expert-select']}
                />
              </div>
            ) : effectiveExpert ? (
              <TeamMemberEntry
                member={effectiveExpert}
                readOnly={true}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  };
  
  // Render a message when no pairings are available
  const renderNoPairingsMessage = () => (
    <p className={styles['no-pairings']}>
      No mentorship pairings available. Pairings are created when team members with "focus" status are matched with experts in the same skill.
    </p>
  );
  
  // Render a message when Growth Zone is empty
  const renderEmptyGrowthZoneMessage = () => (
    <p className={styles['no-pairings']}>
      No growth zone pairings found. To see your growth areas, select yourself in the filter and set focus areas in the skill matrix.
    </p>
  );
  
  // Render a message when Mentoring is empty
  const renderEmptyMentoringMessage = () => (
    <p className={styles['no-pairings']}>
      No mentoring assignments found. Mentoring assignments are created when you're selected as an expert for someone else's growth focus.
    </p>
  );

  return (
    <div className={styles.mentoring__container}>
      <div className={styles['mentoring__controls']}>
        <SegmentedControl 
          segments={viewSegments}
          activeSegment={groupBy}
          onSegmentChange={setGroupBy}
        />
          <ChipFilter
            teamMembers={teamMembers}
            selectedMembers={selectedMembers}
            onChipClick={handleChipClick}
          />
      </div>
        
      <div className={styles['content-container']}>
        {renderPairings()}
      </div>
    </div>
  );
}

export default MentoringPage; 